import { Component, ojs, app, state, v } from "modular-openscriptjs";
import { formatDateForHumans, either, isMobile, store, getStoreSync } from "../../../helpers";

const h = app("h");

export default class AudioPlayerView extends Component {
    isPlaying = state(false);
    currentTime = state(0);
    duration = state(0);
    playbackRate = state(parseFloat(getStoreSync("ca_audio_player_speed_preference") || 1.0));
    volume = state(parseFloat(getStoreSync("ca_audio_player_volume_preference") || 1.0));
    isMuted = state(getStoreSync("ca_audio_player_muted_preference") === "true");

    audio = null;

    constructor() {
        super();
        this.audio = new Audio();

        // Apply saved preferences to the native audio element
        this.audio.playbackRate = this.playbackRate.value;
        this.audio.volume = this.volume.value;
        this.audio.muted = this.isMuted.value;


        // Audio event listeners
        this.audio.addEventListener("timeupdate", () => {
            this.currentTime.value = this.audio.currentTime;
        });

        this.audio.addEventListener("loadedmetadata", () => {
            this.duration.value = this.audio.duration;
        });

        this.audio.addEventListener("play", () => {
            this.isPlaying.value = true;
        });

        this.audio.addEventListener("pause", () => {
            this.isPlaying.value = false;
        });

        this.audio.addEventListener("ended", () => {
            this.isPlaying.value = false;
            this.currentTime.value = 0;
            this.audio.currentTime = 0;
        });
    }

    render(order, ...args) {
        if (!order) return h.empty();

        const audioUrl = order?.audio_url || order?.pivot?.audio_url;
        if (!audioUrl) {
            return h.div(
                { class: "p-4 text-center text-muted" },
                h.i({
                    class: "bi bi-exclamation-triangle fs-2 d-block mb-2 text-warning",
                }),
                h.span("Audio file is not available for this order."),
            );
        }

        // Set or update audio source if it changed
        if (this.audio.src !== audioUrl) {
            this.audio.src = audioUrl;
            this.audio.load();
            this.currentTime.value = 0;

            // Re-apply saved preferences to the native audio element
            this.audio.playbackRate = this.playbackRate.value;
            this.audio.volume = this.volume.value;
            this.audio.muted = this.isMuted.value;


            // Instantly start playing the audio since this is triggered by a user click gesture
            this.audio
                .play()
                .then(() => {
                    this.isPlaying.value = true;
                })
                .catch((err) => {
                    console.warn("Autoplay failed:", err);
                });
        } else if (this.audio.paused) {
            this.audio
                .play()
                .then(() => {
                    this.isPlaying.value = true;
                })
                .catch((err) => {
                    console.warn("Autoplay failed:", err);
                });
        }

        return h.div(
            { class: "p-1" },
            // Scoped Style block for organic fluid paint movement and white controls
            h.style(`
                @keyframes fluidPaint {
                    0% {
                        background-position: 0% 50%, 50% 100%, 100% 0%, 50% 50%;
                        background-size: 150% 150%, 160% 160%, 140% 140%, 150% 150%;
                    }
                    25% {
                        background-position: 100% 0%, 0% 50%, 50% 100%, 100% 100%;
                        background-size: 170% 170%, 140% 140%, 160% 160%, 130% 130%;
                    }
                    50% {
                        background-position: 50% 100%, 100% 50%, 0% 0%, 0% 100%;
                        background-size: 140% 140%, 150% 150%, 170% 170%, 160% 160%;
                    }
                    75% {
                        background-position: 100% 100%, 50% 0%, 100% 50%, 0% 50%;
                        background-size: 160% 160%, 130% 130%, 150% 150%, 170% 170%;
                    }
                    100% {
                        background-position: 0% 50%, 50% 100%, 100% 0%, 50% 50%;
                        background-size: 150% 150%, 160% 160%, 140% 140%, 150% 150%;
                    }
                }
                .fluid-paint-card {
                    background-image: 
                        radial-gradient(circle at 10% 20%, rgba(13, 110, 253, 0.75) 0%, transparent 60%),
                        radial-gradient(circle at 90% 10%, rgba(111, 66, 193, 0.75) 0%, transparent 65%),
                        radial-gradient(circle at 80% 90%, rgba(214, 51, 132, 0.8) 0%, transparent 60%),
                        radial-gradient(circle at 5% 85%, rgba(0, 180, 216, 0.75) 0%, transparent 60%);
                    background-color: #03045e;
                    background-size: 150% 150%, 150% 150%, 150% 150%, 150% 150%;
                    box-shadow: 0 10px 30px rgba(13, 110, 253, 0.25);
                    transition: all 0.5s ease;
                }
                .fluid-paint-card.playing {
                    animation: fluidPaint 6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
                }
                /* Manual Custom Play Button Styling */
                .custom-play-btn {
                    width: 72px;
                    height: 72px;
                    border: 3px solid #ffffff !important;
                    background-color: transparent !important;
                    background: transparent !important;
                    color: #ffffff !important;
                    border-radius: 50% !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    cursor: pointer;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    outline: none !important;
                    padding: 0 !important;
                }
                .custom-play-btn:hover {
                    transform: scale(1.08);
                    box-shadow: 0 12px 28px rgba(255, 255, 255, 0.25);
                    border-color: #ffffff !important;
                    background-color: transparent !important;
                    background: transparent !important;
                }
                .custom-play-btn:active {
                    transform: scale(0.95);
                }
                /* Custom styled speed selector dropdown */
                .custom-speed-select {
                    background-color: transparent !important;
                    background: transparent !important;
                    border: 1.5px solid rgba(255, 255, 255, 0.4) !important;
                    color: #ffffff !important;
                    font-size: 12px !important;
                    font-weight: 700 !important;
                    padding: 6px 28px 6px 12px !important;
                    border-radius: 50px !important;
                    cursor: pointer;
                    outline: none !important;
                    min-width: 85px;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e") !important;
                    background-repeat: no-repeat !important;
                    background-position: right 10px center !important;
                    background-size: 10px !important;
                    transition: all 0.2s ease;
                }
                .custom-speed-select:hover {
                    border-color: rgba(255, 255, 255, 0.8) !important;
                    box-shadow: 0 0 8px rgba(255, 255, 255, 0.1);
                }
                .custom-speed-select:focus {
                    border-color: #ffffff !important;
                }
                .custom-speed-select option {
                    background-color: #03045e !important;
                    color: #ffffff !important;
                }

                /* White styling for range sliders (timeline and volume) */
                .custom-audio-range {
                    -webkit-appearance: none;
                    appearance: none;
                    background: rgba(255, 255, 255, 0.3);
                    width: 100%;
                    height: 6px;
                    border-radius: 3px;
                    outline: none;
                    transition: background 0.1s ease;
                }
                .custom-audio-range::-webkit-slider-runnable-track {
                    background: transparent !important;
                    height: 6px;
                    border-radius: 3px;
                }
                .custom-audio-range::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    background: #ffffff !important;
                    height: 16px;
                    width: 16px;
                    border-radius: 50%;
                    margin-top: -5px;
                    cursor: pointer;
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                }
                .custom-audio-range::-moz-range-track {
                    background: transparent !important;
                    height: 6px;
                    border-radius: 3px;
                }
                .custom-audio-range::-moz-range-thumb {
                    background: #ffffff !important;
                    height: 16px;
                    width: 16px;
                    border-radius: 50%;
                    cursor: pointer;
                    border: none;
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                }
                .custom-audio-range::-moz-range-progress {
                    background: #fed27d !important;
                    height: 6px;
                    border-radius: 3px 0 0 3px;
                }
            `),

            // Header
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4 border-bottom pb-2 sticky-top bg-white",
                },
                h.div(
                    h.h5(
                        {
                            class: "fw-bold mb-0 text-dark d-flex align-items-center gap-2",
                        },
                        h.i({ class: "bi bi-music-note-beamed text-primary" }),
                        `Listening to #${order.order_code || order.id}`,
                    ),
                ),
                h.button(
                    {
                        class: "btn-close",
                        listeners: {
                            click: () => {
                                this.audio.pause();
                            },
                        },
                    },
                    either(
                        isMobile(),
                        { data_bs_dismiss: "offcanvas" },
                        { data_bs_dismiss: "modal" },
                    ),
                ),
            ),

            // Premium Audio Player Dashboard Controls
            v(this.isPlaying, (playing) => {
                const isPlayingVal = playing.value;

                return h.div(
                    {
                        class: `card border-0 shadow-lg rounded-4 p-4 text-center text-white fluid-paint-card ${isPlayingVal ? "playing" : ""}`,
                    },

                    // Track Timeline (Progress Bar)
                    h.div(
                        { class: "mb-4 w-100" },
                        v(this.currentTime, (curr) => {
                            return v(this.duration, (dur) => {
                                const curTime = curr.value || 0;
                                const totalTime = dur.value || 1;
                                const percent = totalTime > 0 ? (curTime / totalTime) * 100 : 0;

                                return h.div(
                                    {},
                                    h.input({
                                        type: "range",
                                        class: "form-range custom-audio-range mb-2",
                                        style: `background: linear-gradient(to right, #fed27d 0%, #fed27d ${percent}%, rgba(255, 255, 255, 0.3) ${percent}%, rgba(255, 255, 255, 0.3) 100%) !important;`,
                                        min: 0,
                                        max: totalTime,
                                        step: 0.1,
                                        value: curTime,
                                        listeners: {
                                            input: (e) => {
                                                const seekVal = parseFloat(
                                                    e.target.value,
                                                );
                                                this.audio.currentTime =
                                                    seekVal;
                                                this.currentTime.value =
                                                    seekVal;
                                            },
                                        },
                                    }),
                                    h.div(
                                        {
                                            class: "d-flex justify-content-between text-white-50 fs-xsm fw-semibold",
                                        },
                                        h.span(this.formatTime(curTime)),
                                        h.span(this.formatTime(totalTime)),
                                    ),
                                );
                            });
                        }),
                    ),

                    // Controls Row (Rewind, Play/Pause, Fast Forward)
                    h.div(
                        {
                            class: "d-flex align-items-center justify-content-center gap-4 mb-4",
                        },

                        // Rewind 10s
                        h.button(
                            {
                                class: "btn btn-outline-light circle border-0 p-2 fs-4",
                                listeners: {
                                    click: () => {
                                        this.audio.currentTime = Math.max(
                                            0,
                                            this.audio.currentTime - 10,
                                        );
                                    },
                                },
                            },
                            h.i({ class: "bi bi-arrow-counterclockwise" }),
                        ),

                        // Big Play/Pause Button
                        h.button(
                            {
                                class: "custom-play-btn",
                                listeners: {
                                    click: () => {
                                        if (isPlayingVal) {
                                            this.audio.pause();
                                        } else {
                                            this.audio.play();
                                        }
                                    },
                                },
                            },
                            h.i({
                                class: `bi ${isPlayingVal ? "bi-pause-fill" : "bi-play-fill"} fs-1 text-white`,
                            }),
                        ),

                        // Fast Forward 10s
                        h.button(
                            {
                                class: "btn btn-outline-light circle border-0 p-2 fs-4",
                                listeners: {
                                    click: () => {
                                        this.audio.currentTime = Math.min(
                                            this.audio.duration || 0,
                                            this.audio.currentTime + 10,
                                        );
                                    },
                                },
                            },
                            h.i({ class: "bi bi-arrow-clockwise" }),
                        ),
                    ),

                    // Secondary Settings: Speed Multipliers & Volume control
                    h.div(
                        {
                            class: "row g-3 align-items-center pt-3 border-top border-white-10",
                        },

                        // Speed selector
                        h.div(
                            { class: "col-6 d-flex align-items-center gap-2" },
                            h.i({
                                class: "bi bi-speedometer2 text-white-50 fs-5",
                            }),
                            v(this.playbackRate, (rate) => {
                                return h.select(
                                    {
                                        class: "custom-speed-select",
                                        listeners: {
                                            change: (e) => {
                                                const newRate = parseFloat(
                                                    e.target.value,
                                                );
                                                this.audio.playbackRate =
                                                    newRate;
                                                this.playbackRate.value =
                                                    newRate;
                                                store("ca_audio_player_speed_preference", newRate);
                                            },
                                        },
                                    },
                                    h.option(
                                        {
                                            value: 0.5,
                                        },
                                        either(rate.value == 0.5, {
                                            selected: true,
                                        }),
                                        "0.5x",
                                    ),
                                    h.option(
                                        {
                                            value: 1.0,
                                        },
                                        either(rate.value == 1.0, {
                                            selected: true,
                                        }),
                                        "1.0x",
                                    ),
                                    h.option(
                                        {
                                            value: 1.25,
                                        },
                                        either(rate.value == 1.25, {
                                            selected: true,
                                        }),
                                        "1.25x",
                                    ),
                                    h.option(
                                        {
                                            value: 1.5,
                                        },
                                        either(rate.value == 1.5, {
                                            selected: true,
                                        }),
                                        "1.5x",
                                    ),
                                    h.option(
                                        {
                                            value: 2.0,
                                        },
                                        either(rate.value == 2.0, {
                                            selected: true,
                                        }),
                                        "2.0x",
                                    ),
                                );
                            }),
                        ),

                        // Volume control
                        h.div(
                            {
                                class: "col-6 d-flex align-items-center gap-2 justify-content-end",
                            },
                            v(this.isMuted, (muted) => {
                                return h.button(
                                    {
                                        class: "btn btn-outline-light border-0 p-1 circle fs-5",
                                        listeners: {
                                            click: () => {
                                                const newMuted = !muted.value;
                                                this.audio.muted = newMuted;
                                                this.isMuted.value = newMuted;
                                                store("ca_audio_player_muted_preference", newMuted);
                                            },
                                        },
                                    },
                                    h.i({
                                        class: `bi ${muted.value ? "bi-volume-mute-fill" : "bi-volume-up-fill"}`,
                                    }),
                                );
                            }),
                            v(this.volume, (vol) => {
                                return h.input({
                                    type: "range",
                                    class: "form-range custom-audio-range flex-fill",
                                    style: "max-width: 90px;",
                                    min: 0,
                                    max: 1,
                                    step: 0.1,
                                    value: vol.value,
                                    listeners: {
                                        input: (e) => {
                                            const newVol = parseFloat(
                                                e.target.value,
                                            );
                                            this.audio.volume = newVol;
                                            this.volume.value = newVol;
                                            this.isMuted.value = newVol === 0;
                                            this.audio.muted = newVol === 0;
                                            store("ca_audio_player_volume_preference", newVol);
                                            store("ca_audio_player_muted_preference", newVol === 0);
                                        },
                                    },
                                });
                            }),
                        ),
                    ),
                );
            }),
        );
    }

    formatTime(time) {
        if (isNaN(time) || time === Infinity) return "00:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
}

ojs(AudioPlayerView);
